import { useParams } from "react-router-dom";
import { InventoryTypeSchema, type InventoryType } from "@starsach/shared-types";
import { useCustomer, useInventory } from "../../api/hooks";
import { EnvironmentGate, useRememberEnvironment } from "../../components/Environment";
import { Hint } from "../../components/Hint";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime } from "../../components/ui";
import { useFormat } from "../../lib/useFormat";
import { moduleById } from "../../lib/modules";
import { ContextBar, InventoryStateNote } from "./ContextBar";

const LABEL: Record<InventoryType, string> = {
  queue: "Queues",
  flow: "Contact flows",
  prompt: "Prompts",
  hours_of_operation: "Hours of operation (Connect)",
  lex_bot: "Lex bots",
  phone_number: "Phone numbers",
};

/**
 * Connect cache (the owner's ask of 2026-09-18, in place of authored
 * references): what the instance actually has - queues, flows, prompts,
 * hours of operation, bots, numbers - read-only, kept current by events
 * (ADR-0018), with whether the instance is settled or mid-change. A flow
 * refers to these by name; a profile never holds an ARN.
 */
export function CachePage() {
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="cache" />;
  return <Cache environmentId={environmentId} />;
}

function Cache({ environmentId }: { environmentId: string }) {
  const fmt = useFormat();
  const env = useCustomer(environmentId);
  const inventory = useInventory(environmentId);
  const envName = env.data?.customer.friendly_name ?? environmentId;
  const items = inventory.data?.items ?? [];
  const state = inventory.data?.state;
  return (
    <>
      <PageHeader title="Flow configuration · Connect cache" />
      <div className="grid gap-5">
        <ContextBar environmentId={environmentId} environmentName={envName} live={env.data?.customer.live ?? false} onEnvironment={(id) => `/flow-configuration/${id}/cache`}>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {inventory.data ? `${items.length} objects` : ""}
            {state ? ` · inventory v${state.version}` : ""}
            {state?.state === "settled" && state.settledAt ? ` · settled ${fmt.time(state.settledAt)}` : ""}
            {inventory.data?.observedAt && (
              <>
                {" "}· read <RelativeTime iso={inventory.data.observedAt} />
              </>
            )}
            <Hint id="flow.inventory-state" label="the Connect cache" />
          </span>
        </ContextBar>
        <InventoryStateNote state={state} environmentName={envName} />
        {inventory.isPending && <LoadingState label="Reading the instance" />}
        {inventory.isError && <ErrorState error={inventory.error} onRetry={() => void inventory.refetch()} />}
        {inventory.data && items.length === 0 && (
          <EmptyState title="Nothing read from the instance yet" hint="The stack publishes the instance's names within about a minute of a change, and re-lists everything hourly. If this stays empty, check that EnableFlowConfiguration is on and the stack's role can list the instance." />
        )}
        {inventory.data && items.length > 0 && (
          <div className="grid gap-5 lg:grid-cols-2">
            {InventoryTypeSchema.options.map((type) => {
              const rows = items.filter((i) => i.type === type);
              return (
                <Card key={type} title={`${LABEL[type]} · ${rows.length}`}>
                  <DataTable
                    caption={`${LABEL[type]} in ${envName}`}
                    rows={rows}
                    rowKey={(r) => r.arn}
                    sortBy={(r) => r.name}
                    filter={rows.length > 5}
                    scrollable
                    empty={<p className="py-2 text-sm text-neutral-500">None in this instance.</p>}
                    columns={[
                      { key: "name", header: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
                      { key: "arn", header: "ARN", render: (r) => <code className="block max-w-md truncate text-[11px] text-neutral-500" title={r.arn}>{r.arn}</code> },
                    ]}
                  />
                </Card>
              );
            })}
          </div>
        )}
        {inventory.data && inventory.data.aliases.length > 0 && (
          <Card title="Aliases">
            <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">Where a name in a profile is called something else in this instance.</p>
            <DataTable
              caption="Aliases in this environment"
              rows={inventory.data.aliases}
              rowKey={(a) => `${a.type}#${a.name}`}
              filter={false}
              empty={<EmptyState title="No aliases" />}
              columns={[
                { key: "name", header: "Profile name", render: (a) => <code className="text-xs">{a.name}</code> },
                { key: "type", header: "Type", render: (a) => LABEL[a.type] },
                { key: "to", header: "Is called here", render: (a) => a.resolvesTo },
                { key: "by", header: "Set by", render: (a) => <>{a.setBy} · <RelativeTime iso={a.setAt} /></> },
              ]}
            />
          </Card>
        )}
      </div>
    </>
  );
}
